<template>
  <div class="handout-preview-page">
    <div class="preview-toolbar">
      <div class="toolbar-left">
        <el-button type="text" icon="el-icon-arrow-left" @click="$router.push(`${handoutBasePath}/${handoutId}`)">返回编辑</el-button>
        <span class="toolbar-title">{{ title || '讲义预览' }}</span>
      </div>
      <div class="toolbar-actions">
        <span v-if="exportQuotaLabel" class="export-quota-label">{{ exportQuotaLabel }}</span>
        <el-radio-group v-model="version" size="small">
          <el-radio-button label="student">学生版</el-radio-button>
          <el-radio-button label="teacher">教师版</el-radio-button>
        </el-radio-group>
        <el-button size="small" icon="el-icon-document" :loading="docxExporting" @click="downloadDocx">下载 Word</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="loadContent(true)">刷新</el-button>
      </div>
    </div>

    <main v-loading="loading" class="preview-shell">
      <HandoutHtmlPreview
        v-if="hasPreviewContent"
        ref="handoutPreview"
        :title="title"
        :content-html="contentHtml"
        :show-question-source="showSource"
        :show-question-difficulty="showDifficulty"
      />
      <el-empty v-else description="暂无预览内容，请先保存讲义正文" />
    </main>

    <HandoutExportPayDialog :visible.sync="exportPayVisible" @paid="onExportPaid" />
  </div>
</template>

<script>
import apiContext from '../../mixins/apiContext'
import handoutExportBilling from '../../mixins/handoutExportBilling'
import HandoutHtmlPreview from '../../components/HandoutHtmlPreview.vue'
import { stripHandoutPaginationComment, encodePaginationPlanParam } from '../../utils/handoutPagination'
import {
  extractHandoutFromResponse,
  loadHandoutPreviewCache,
  saveHandoutPreviewCache
} from '../../utils/handoutPreviewCache'
import { downloadHandoutExport } from '../../utils/handoutExportDownload'
import { ExportQuotaError } from '../../utils/teacherExportBilling'

export default {
  name: 'HandoutPreview',
  components: { HandoutHtmlPreview },
  mixins: [apiContext, handoutExportBilling],
  data() {
    return {
      loading: false,
      title: '',
      contentHtml: '',
      version: 'student',
      showSource: true,
      showDifficulty: true,
      blankPageBreak: false,
      appendQuestions: false,
      alignBodyPages: true,
      docxExporting: false
    }
  },
  computed: {
    handoutId() { return this.$route.params.id },
    handoutBasePath() { return this.isTeacherMode ? '/teacher/handout' : '/handout' },
    versionLabel() { return this.version === 'teacher' ? '教师版' : '学生版' },
    hasPreviewContent() {
      const body = stripHandoutPaginationComment(this.contentHtml || '').trim()
      return !!(body || (this.title || '').trim())
    }
  },
  created() {
    this.applyQueryOptions()
    this.applyCachedContent()
    this.loadContent()
  },
  methods: {
    questionMetaVisibilityStorageKey() {
      return `handout:${this.handoutId}:questionMetaVisibility`
    },
    loadStoredQuestionMetaVisibility() {
      try {
        const raw = localStorage.getItem(this.questionMetaVisibilityStorageKey())
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        return null
      }
    },
    applyQueryOptions() {
      const boolFromQuery = (value, fallback) => {
        if (value === 'true') return true
        if (value === 'false') return false
        return fallback
      }
      const stored = this.loadStoredQuestionMetaVisibility() || {}
      this.showSource = boolFromQuery(this.$route.query.showSource, stored.showSource != null ? stored.showSource : this.showSource)
      this.showDifficulty = boolFromQuery(this.$route.query.showDifficulty, stored.showDifficulty != null ? stored.showDifficulty : this.showDifficulty)
      this.blankPageBreak = boolFromQuery(this.$route.query.blankPageBreak, this.blankPageBreak)
      this.appendQuestions = boolFromQuery(this.$route.query.appendQuestions, this.appendQuestions)
      this.alignBodyPages = boolFromQuery(this.$route.query.alignBodyPages, this.alignBodyPages)
      if (this.$route.query.version === 'teacher' || this.$route.query.version === 'student') {
        this.version = this.$route.query.version
      }
    },
    applyHandout(handout) {
      if (!handout || typeof handout !== 'object') return
      if (handout.title) this.title = handout.title
      if (handout.contentHtml) this.contentHtml = handout.contentHtml
    },
    applyCachedContent() {
      const cached = loadHandoutPreviewCache(this.handoutId)
      if (cached) this.applyHandout(cached)
    },
    loadContent(forceRemote = false) {
      const cached = loadHandoutPreviewCache(this.handoutId)
      const cachedBody = stripHandoutPaginationComment(cached && cached.contentHtml ? cached.contentHtml : '').trim()
      if (!forceRemote && cachedBody) {
        this.applyHandout(cached)
        return
      }
      this.loading = true
      this.api.get(`/handout/${this.handoutId}`).then(data => {
        const handout = extractHandoutFromResponse(data)
        const remoteBody = stripHandoutPaginationComment(handout.contentHtml || '').trim()
        const remoteTitle = (handout.title || '').trim()
        if (remoteBody || remoteTitle) {
          this.applyHandout(handout)
          saveHandoutPreviewCache(this.handoutId, {
            title: this.title,
            contentHtml: this.contentHtml
          })
          return
        }
        if (!this.hasPreviewContent) {
          this.applyCachedContent()
        }
      }).catch(() => {
        if (!this.hasPreviewContent) {
          this.applyCachedContent()
        }
        if (!this.hasPreviewContent) {
          this.$message.error('预览加载失败，请确认讲义已保存')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    buildExportQuery() {
      const params = new URLSearchParams()
      params.set('version', this.version)
      params.set('showSource', this.showSource ? 'true' : 'false')
      params.set('showDifficulty', this.showDifficulty ? 'true' : 'false')
      params.set('blankPageBreak', this.blankPageBreak ? 'true' : 'false')
      params.set('appendQuestions', this.appendQuestions ? 'true' : 'false')
      params.set('alignBodyPages', 'true')
      const planParam = encodePaginationPlanParam(this.contentHtml || '')
      if (planParam) params.set('paginationPlan', planParam)
      return params.toString()
    },
    async downloadDocx() {
      this.docxExporting = true
      const exec = async () => {
        await downloadHandoutExport(this.handoutId, {
          version: this.version,
          format: 'docx',
          query: this.buildExportQuery(),
          title: this.title,
          contentHtml: this.contentHtml,
          apiBase: this.apiPrefix,
          onQuotaExceeded: () => this.openExportPayDialog(exec)
        })
        this.$message.success('Word 已下载')
        if (this.isTeacherMode) await this.loadExportQuota()
      }
      try {
        await exec()
      } catch (e) {
        if (!(e instanceof ExportQuotaError)) {
          this.$message.error('Word 下载失败')
        }
      } finally {
        this.docxExporting = false
      }
    }
  }
}
</script>

<style scoped>
.handout-preview-page { height: 100vh; background: #eef1f5; display: flex; flex-direction: column; overflow: hidden; }
.preview-toolbar { height: 64px; padding: 0 24px; background: #fff; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-shrink: 0; }
.toolbar-left, .toolbar-actions { display: flex; align-items: center; gap: 10px; min-width: 0; }
.toolbar-title { font-size: 16px; font-weight: 700; color: #1f2937; max-width: 560px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.export-quota-label { font-size: 12px; color: #909399; }
.preview-shell { flex: 1; min-height: 0; overflow: hidden; }

@media (max-width: 720px) {
  .preview-toolbar { height: auto; min-height: 64px; padding: 10px 14px; flex-wrap: wrap; }
  .toolbar-title { max-width: 100%; }
  .toolbar-actions { width: 100%; justify-content: flex-start; flex-wrap: wrap; }
}
</style>
